// MARK://dhk8/pos/features/floor/TableSheet
// Bottom sheet shown when a table is tapped on /floor (view mode). Shows:
//   • table code + capacity + current state (E1 model)
//   • the open order (folio/total/opened-at) if any
//   • PENDING guest notifications for THIS table (E2) — monitoreo rápido
//   • status-change buttons covering the full state model: disponible /
//     ocupada / reservada / sucia (limpiar) / fuera de servicio
//   • Abrir / Ver comanda
// Edit mode keeps capacity + shape editing. Dismiss by tapping the backdrop.

import { useEffect, useState } from 'react'
import { X, Check, Copy, Move, Lock as LockIcon, Users as UsersIcon, ExternalLink } from 'lucide-react'
import type { TableDto } from '@dockethub/ui-restaurant'
import { formatCents } from '../../lib/money.js'
import type {
  GuestNotificationDto,
  GuestNotificationTopic,
} from '../../lib/clients/floor.js'

// E1 — full table-state model. `DIRTY` kept as a legacy alias of `CLEANING`.
const STATUS_LABEL: Record<TableDto['status'], string> = {
  AVAILABLE: 'Disponible',
  OCCUPIED: 'Ocupada',
  RESERVED: 'Reservada',
  DIRTY: 'Por limpiar',
  CLEANING: 'Por limpiar',
  UNAVAILABLE: 'Fuera de servicio',
}

/** Canonical state set the staff can set from /floor (DIRTY is normalized out). */
export type SettableStatus = 'AVAILABLE' | 'OCCUPIED' | 'RESERVED' | 'CLEANING' | 'UNAVAILABLE'

// Which transitions are offered from each current state. The full model is
// always reachable — restaurants need to override (e.g. mark a reserved table
// out of service). We just order/pre-select the *sensible* ones first.
const TRANSITIONS: Record<TableDto['status'], SettableStatus[]> = {
  // Lane #176 — OCCUPIED queda FUERA de los cambios manuales: una mesa sólo
  // pasa a OCCUPIED al abrir sesión (apertura de orden). Si el staff la marca
  // a mano sin session, /order la muestra como "Ocupada (sin sesión)" sin
  // forma de retomarla → loophole zombie inverso. Apertura siempre vía
  // openTableSession, liberación siempre vía cobro / cancelación / forceClear.
  AVAILABLE: ['RESERVED', 'CLEANING', 'UNAVAILABLE'],
  OCCUPIED: ['CLEANING', 'AVAILABLE', 'UNAVAILABLE'],
  RESERVED: ['AVAILABLE', 'UNAVAILABLE'],
  DIRTY: ['AVAILABLE', 'UNAVAILABLE'],
  CLEANING: ['AVAILABLE', 'UNAVAILABLE'],
  UNAVAILABLE: ['AVAILABLE'],
}

const SHAPES: NonNullable<TableDto['shape']>[] = ['RECT', 'ROUND']

const TOPIC_META: Record<GuestNotificationTopic, { icon: string; label: string }> = {
  CALL_WAITER: { icon: '🙋', label: 'Llamar al mesero' },
  REQUEST_CHECK: { icon: '🧾', label: 'Pedir la cuenta' },
  REQUEST_ITEM: { icon: '🍴', label: 'Pedir algo más' },
  REPORT_ISSUE: { icon: '⚠️', label: 'Reportar un problema' },
  OTHER: { icon: '💬', label: 'Otro' },
}

export interface OpenOrderSummary {
  id: string
  folio: string
  totalCents: number
  openedAt: string
}

function minutesAgo(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime()
  const m = Math.max(0, Math.round(ms / 60_000))
  if (m < 1) return 'ahora'
  if (m < 60) return `hace ${m} min`
  const h = Math.floor(m / 60)
  return `hace ${h} h ${m % 60} min`
}

export interface TableSheetProps {
  table: TableDto
  editing: boolean
  order?: OpenOrderSummary
  /** ISO of an active session opened-at (when there's a session but no resolved order). */
  sessionOpenedAt?: string
  /** Lane #165 — covers de la sesión (para mostrar en el modal info). */
  sessionCovers?: number
  /** Lane #165 — joinCode del módulo guest (OhSir, 6 chars). null si no hay
   *  o si la sesión la abrió staff y nadie escaneó QR. */
  sessionJoinCode?: string | null
  /** Lane #165 — orderId de la sesión activa, para builds del wa.me/share-link. */
  sessionOrderId?: string | null
  /** Lane #165 — tenant slug para builds del link guest (guest.nintaigo.com/<slug>/...). */
  tenantSlug?: string | null
  /** E2 — PENDING guest notifications for THIS table. */
  notifications?: GuestNotificationDto[]
  /** Resolve one notification (marca atendida). */
  onResolveNotification?: (id: string) => void
  busyNotificationId?: string | null
  onClose: () => void
  onChangeStatus: (status: SettableStatus) => void
  onOpenOrder: () => void
  onViewOrder: () => void
  /** Lane #165 — abrir el dialogo "mover orden a otra mesa". */
  onMoveOrder?: () => void
  /** Lane #172 — forzar liberar la mesa (manager-gated con razón obligatoria).
   *  Cancela la orden activa y deja la mesa AVAILABLE. */
  onForceClear?: () => void
  /** Edit mode only. */
  onPatchTable?: (patch: Partial<Pick<TableDto, 'capacity' | 'shape'>>) => void
  onDeleteTable?: () => void
}

export function TableSheet({
  table,
  editing,
  order,
  sessionOpenedAt,
  sessionCovers,
  sessionJoinCode,
  sessionOrderId,
  tenantSlug,
  notifications = [],
  onResolveNotification,
  busyNotificationId,
  onClose,
  onChangeStatus,
  onOpenOrder,
  onViewOrder,
  onMoveOrder,
  onForceClear,
  onPatchTable,
  onDeleteTable,
}: TableSheetProps) {
  // Close on Escape.
  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', h)
    return () => window.removeEventListener('keydown', h)
  }, [onClose])

  // String-backed draft so the user can vaciar el campo y reescribir (sin que
  // un default `1` se quede pegado). Commit at blur on a valid integer.
  const [capDraft, setCapDraft] = useState<string>(String(table.capacity))
  useEffect(() => { setCapDraft(String(table.capacity)) }, [table.capacity])
  function bumpCap(delta: number) {
    const base = capDraft.trim() === '' || !Number.isFinite(Number(capDraft)) ? table.capacity : Number(capDraft)
    const next = Math.max(1, Math.min(40, Math.trunc(base) + delta))
    setCapDraft(String(next))
    if (next !== table.capacity) onPatchTable?.({ capacity: next })
  }
  function commitCapBlur() {
    if (capDraft.trim() === '' || !Number.isFinite(Number(capDraft))) {
      setCapDraft(String(table.capacity))
      return
    }
    const clamped = Math.max(1, Math.min(40, Math.trunc(Number(capDraft))))
    setCapDraft(String(clamped))
    if (clamped !== table.capacity) onPatchTable?.({ capacity: clamped })
  }

  const openedAt = order?.openedAt ?? sessionOpenedAt
  const isOccupied = table.status === 'OCCUPIED'
  const canOpen = table.status === 'AVAILABLE' || table.status === 'RESERVED'
  // Lane #165 — si la mesa tiene sesión activa (orden abierta), el staff NO
  // puede cambiar status libremente. La mesa se libera al cobrar/cancelar la
  // orden. Es la regla operativa para no perder ítems sin contabilizar.
  const hasActiveSession = !!order || !!sessionOpenedAt
  const [copiedCode, setCopiedCode] = useState(false)
  function copyJoinCode() {
    if (!sessionJoinCode) return
    try {
      void navigator.clipboard.writeText(sessionJoinCode)
      setCopiedCode(true)
      setTimeout(() => setCopiedCode(false), 1500)
    } catch { /* clipboard not available */ }
  }
  // Lane #165 — wa.me deep link al guest si tenemos slug+joinCode.
  const guestShareUrl = sessionJoinCode && tenantSlug
    ? `https://guest.nintaigo.com/${encodeURIComponent(tenantSlug)}/join/${encodeURIComponent(sessionJoinCode)}`
    : null
  function shareGuestLink() {
    if (!guestShareUrl) return
    const text = `Pide desde tu celular en esta mesa:\n${guestShareUrl}\nCódigo: ${sessionJoinCode}`
    try {
      if (typeof navigator !== 'undefined' && 'share' in navigator) {
        void (navigator as Navigator & { share: (d: { text: string }) => Promise<void> }).share({ text })
        return
      }
    } catch { /* fall through */ }
    // Fallback: wa.me deep-link (abre WhatsApp con el texto pre-cargado).
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank', 'noopener')
  }
  // Avoid the duplicated "Mesa Mesa 4" when the code itself already starts
  // with "mesa" (the seedDemo convention is `Mesa 1`, `Mesa 2`, ...).
  const tableTitle = /^mesa\b/i.test(table.code) ? table.code : `Mesa ${table.code}`

  return (
    <div className="dhk8-tsheet" role="dialog" aria-modal="true" aria-label={tableTitle}>
      <button type="button" className="dhk8-tsheet__backdrop" aria-label="Cerrar" onClick={onClose} />
      <div className="dhk8-tsheet__panel">
        <div className="dhk8-tsheet__grip" aria-hidden="true" />
        <div className="dhk8-tsheet__head">
          <div>
            <div className="dhk8-tsheet__title">{tableTitle}</div>
            <div className="dhk8-tsheet__sub">
              <span className={`dhk8-tsheet__state-pill dhk8-tsheet__state-pill--${table.status.toLowerCase()}`}>
                <span className="dhk8-tsheet__state-dot" aria-hidden="true" />
                {STATUS_LABEL[table.status]}
              </span>
              <span>{table.capacity} personas</span>
            </div>
          </div>
          <button type="button" className="dhk8-tsheet__x" aria-label="Cerrar" onClick={onClose}><X size={18} /></button>
        </div>

        {(order || openedAt) && (
          <div className="dhk8-tsheet__order">
            {order ? (
              <>
                <span className="dhk8-tsheet__order-folio">Comanda {order.folio}</span>
                <span className="dhk8-tsheet__order-total">{formatCents(order.totalCents)}</span>
                <span className="dhk8-tsheet__order-when">{minutesAgo(order.openedAt)}</span>
              </>
            ) : (
              <>
                <span className="dhk8-tsheet__order-folio">Sesión abierta</span>
                <span className="dhk8-tsheet__order-when">{openedAt ? minutesAgo(openedAt) : ''}</span>
              </>
            )}
          </div>
        )}

        {/* Lane #165 — Info compartible del módulo guest. Solo aparece si hay
            sesión activa y un joinCode ya minteado. */}
        {hasActiveSession && sessionJoinCode && (
          <div className="dhk8-tsheet__guest">
            <div className="dhk8-tsheet__guest-head">
              <span className="dhk8-tsheet__section-label" style={{ margin: 0 }}>Código guest · grupo</span>
              {sessionCovers !== undefined && (
                <span className="dhk8-tsheet__guest-covers"><UsersIcon size={11} /> {sessionCovers}</span>
              )}
            </div>
            <div className="dhk8-tsheet__guest-code-row">
              <button
                type="button"
                className="dhk8-tsheet__guest-code"
                onClick={copyJoinCode}
                title={copiedCode ? 'Copiado' : 'Copiar al portapapeles'}
              >
                <span className="dhk8-tsheet__guest-code-val">{sessionJoinCode}</span>
                <Copy size={13} />
                {copiedCode && <span className="dhk8-tsheet__guest-copied">¡Copiado!</span>}
              </button>
              {guestShareUrl && (
                <button
                  type="button"
                  className="dhk8-tsheet__guest-share"
                  onClick={shareGuestLink}
                  title="Compartir link (WhatsApp / Sistema)"
                >
                  <ExternalLink size={13} /> Compartir
                </button>
              )}
            </div>
            <p className="dhk8-tsheet__guest-hint">
              Dictá este código al grupo para que pidan desde su celular en{' '}
              <strong>guest.nintaigo.com</strong>.
            </p>
          </div>
        )}

        {/* Acción de orden unificada: vive SOLO en el footer (dhk8-tsheet__actions
            al final). Antes había un segundo bloque aquí que duplicaba el CTA
            ("Continuar pedido" arriba + "Abrir comanda" abajo = mismo onOpenOrder). */}

        {notifications.length > 0 && (
          <>
            <div className="dhk8-tsheet__section-label">
              Avisos del comensal · {notifications.length}
            </div>
            <ul className="dhk8-tsheet__notifs">
              {notifications.map((n) => {
                const meta = TOPIC_META[n.topic] ?? TOPIC_META.OTHER
                return (
                  <li key={n.id} className="dhk8-tsheet__notif">
                    <span className="dhk8-tsheet__notif-icon" aria-hidden="true">{meta.icon}</span>
                    <div className="dhk8-tsheet__notif-body">
                      <span className="dhk8-tsheet__notif-topic">{meta.label}</span>
                      {n.note && <span className="dhk8-tsheet__notif-note">“{n.note}”</span>}
                      <span className="dhk8-tsheet__notif-when">{minutesAgo(n.createdAt)}</span>
                    </div>
                    {onResolveNotification && (
                      <button
                        type="button"
                        className="dhk8-tsheet__notif-btn"
                        disabled={busyNotificationId === n.id}
                        onClick={() => onResolveNotification(n.id)}
                        title="Marcar como atendida"
                      >
                        <Check size={13} /> Atendida
                      </button>
                    )}
                  </li>
                )
              })}
            </ul>
          </>
        )}

        {/* Lane #165 — Cambiar estado bloqueado cuando hay sesión activa.
            Las mesas con cuenta abierta solo se liberan al cobrar o cancelar
            la orden — evita perder ítems sin contabilizar. */}
        {!hasActiveSession ? (
          <>
            <div className="dhk8-tsheet__section-label">Cambiar estado</div>
            <div className="dhk8-tsheet__statuses">
              {TRANSITIONS[table.status].map((s) => (
                <button
                  key={s}
                  type="button"
                  className={`dhk8-tsheet__status-btn dhk8-tsheet__status-btn--${s.toLowerCase()}`}
                  onClick={() => onChangeStatus(s)}
                >
                  <span className="dhk8-tsheet__status-dot" aria-hidden="true" />
                  {STATUS_LABEL[s]}
                </button>
              ))}
            </div>
          </>
        ) : (
          <>
            <div className="dhk8-tsheet__lock-banner">
              <LockIcon size={13} />
              <span>
                Mesa con cuenta abierta — el estado se libera al{' '}
                <strong>cobrar</strong> o <strong>cancelar</strong> la comanda.
                {onMoveOrder && order && <> Si necesitas otra mesa, usa <strong>Mover</strong>.</>}
              </span>
            </div>
            {/* Lane #172 — escape hatch para situaciones reales: cliente se fue
                sin pagar, mesera olvidó cancelar, prueba de soft launch que se
                bloqueó. Pide PIN de gerente + razón obligatoria; al aprobar
                cancela la orden y libera la mesa. */}
            {onForceClear && (
              <button
                type="button"
                className="dhk8-tsheet__force-clear"
                onClick={onForceClear}
              >
                Forzar liberar mesa (gerente)
              </button>
            )}
          </>
        )}

        {editing && onPatchTable && (
          <>
            <div className="dhk8-tsheet__section-label">Editar mesa</div>
            <div className="dhk8-tsheet__edit">
              <label className="dhk8-tsheet__field">
                <span>Capacidad</span>
                <div className="dhk8-tsheet__num">
                  <button
                    type="button"
                    className="dhk8-tsheet__num-btn"
                    onClick={() => bumpCap(-1)}
                    aria-label="Disminuir capacidad"
                    disabled={table.capacity <= 1}
                  >−</button>
                  <input
                    type="number" min={1} max={40} step={1} inputMode="numeric"
                    value={capDraft}
                    onChange={(e) => setCapDraft(e.target.value)}
                    onBlur={commitCapBlur}
                  />
                  <button
                    type="button"
                    className="dhk8-tsheet__num-btn"
                    onClick={() => bumpCap(1)}
                    aria-label="Aumentar capacidad"
                    disabled={table.capacity >= 40}
                  >+</button>
                </div>
              </label>
              <label className="dhk8-tsheet__field">
                <span>Forma</span>
                <select value={table.shape ?? 'RECT'} onChange={(e) => onPatchTable({ shape: e.target.value as TableDto['shape'] })}>
                  {SHAPES.map((s) => <option key={s} value={s}>{s === 'ROUND' ? 'Redonda' : 'Rectangular'}</option>)}
                </select>
              </label>
            </div>
            {onDeleteTable && (
              <button type="button" className="dhk8-tsheet__danger" onClick={() => { onDeleteTable(); onClose() }}>
                Eliminar mesa
              </button>
            )}
          </>
        )}

        {!editing && (
          <div className="dhk8-tsheet__actions dhk8-tsheet__actions--footer">
            {hasActiveSession ? (
              <>
                <button type="button" className="dhk8-tsheet__primary" onClick={onOpenOrder}>
                  {order ? 'Abrir comanda' : 'Continuar pedido'}
                </button>
                {onMoveOrder && order && (
                  <button type="button" className="dhk8-tsheet__action" onClick={onMoveOrder}>
                    <Move size={14} /> Mover
                  </button>
                )}
              </>
            ) : isOccupied ? (
              <button type="button" className="dhk8-tsheet__primary" onClick={onViewOrder}>Ver comanda</button>
            ) : canOpen ? (
              <button type="button" className="dhk8-tsheet__primary" onClick={onOpenOrder}>Abrir comanda</button>
            ) : null}
          </div>
        )}
      </div>
      <style>{styles}</style>
    </div>
  )
}

const styles = `
  /* NintaiGO design rules enforced below:
       - NO border-radius (excepción ÚNICA: dots de estado).
       - NO box-shadow. Elevación = 1 px border o inversión bg/fg. */
  .dhk8-tsheet { position: fixed; inset: 0; z-index: var(--pos-z-modal, 100); display: flex; flex-direction: column; justify-content: flex-end; }
  .dhk8-tsheet__backdrop { position: absolute; inset: 0; background: var(--pos-scrim, rgba(0,0,0,.5)); border: 0; padding: 0; cursor: pointer; }
  .dhk8-tsheet__panel {
    position: relative; width: 100%; box-sizing: border-box;
    background: var(--pos-elevated, #1E2520);
    /* Esquinas RECTAS — sin radius en la sheet. */
    border-top: 1px solid var(--pos-separator-strong, rgba(255,255,255,.14));
    border-left: 1px solid var(--pos-separator-strong, rgba(255,255,255,.14));
    border-right: 1px solid var(--pos-separator-strong, rgba(255,255,255,.14));
    padding: var(--pos-space-3, 12px) var(--pos-space-4, 16px) calc(var(--pos-space-5, 20px) + env(safe-area-inset-bottom, 0px));
    display: flex; flex-direction: column; gap: var(--pos-space-3, 12px);
    max-height: 84dvh; overflow-y: auto;
  }
  /* Escritorio: la sheet a todo el ancho se veía enorme/horrible. La acotamos y
     centramos como tarjeta; las acciones del footer van en fila, no apiladas. */
  @media (min-width: 720px) {
    .dhk8-tsheet__panel { max-width: 560px; margin: 0 auto; border: 1px solid var(--pos-separator-strong, rgba(255,255,255,.14)); }
    .dhk8-tsheet__actions--footer { flex-direction: row; }
    .dhk8-tsheet__actions--footer .dhk8-tsheet__primary { flex: 1; }
  }
  /* "Grip" indicator at the top — square 36×4 bar (was a rounded pill). */
  .dhk8-tsheet__grip { align-self: center; width: 36px; height: 3px; background: var(--pos-separator-strong, rgba(255,255,255,.2)); }
  .dhk8-tsheet__head { display: flex; align-items: flex-start; justify-content: space-between; gap: var(--pos-space-3, 12px); }
  .dhk8-tsheet__title { font-family: var(--font-display, system-ui); font-size: var(--pos-text-xl, 20px); font-weight: var(--pos-weight-semibold, 600); color: var(--pos-fg, #E8EFE9); }
  .dhk8-tsheet__sub { display: flex; align-items: center; gap: var(--pos-space-2, 8px); font-size: var(--pos-text-sm, 13px); color: var(--pos-fg-tertiary, #7E8C82); margin-top: 6px; }

  /* State chip — rectangular (NO pill), 1 px border, status dot is the ONLY round element. */
  .dhk8-tsheet__state-pill {
    display: inline-flex; align-items: center; gap: 6px;
    padding: 3px 9px;
    font-size: var(--pos-text-xs, 12px); font-weight: var(--pos-weight-semibold, 600);
    background: var(--pos-surface, #161C18); border: 1px solid var(--pos-separator-strong, rgba(255,255,255,.14));
    color: var(--pos-fg-secondary, #B6BFB8);
  }
  /* DOT — única excepción autorizada al rule de no-radius. */
  .dhk8-tsheet__state-dot { width: 9px; height: 9px; border-radius: var(--radius-dot); flex: 0 0 auto; background: var(--pos-fg-tertiary, #7E8C82); }
  .dhk8-tsheet__state-pill--available .dhk8-tsheet__state-dot { background: var(--pos-success, #2EA043); }
  .dhk8-tsheet__state-pill--occupied .dhk8-tsheet__state-dot { background: var(--pos-danger, #E5484D); }
  .dhk8-tsheet__state-pill--reserved .dhk8-tsheet__state-dot { background: var(--pos-warning, #E0A23C); }
  .dhk8-tsheet__state-pill--dirty .dhk8-tsheet__state-dot,
  .dhk8-tsheet__state-pill--cleaning .dhk8-tsheet__state-dot { background: var(--pos-info, #4493F8); }
  .dhk8-tsheet__state-pill--unavailable .dhk8-tsheet__state-dot { background: var(--pos-fg-tertiary, #7E8C82); }

  .dhk8-tsheet__order {
    display: flex; flex-wrap: wrap; align-items: baseline; gap: var(--pos-space-2, 8px) var(--pos-space-3, 12px);
    padding: var(--pos-space-3, 12px);
    background: var(--pos-surface, #161C18); border: 1px solid var(--pos-separator, rgba(255,255,255,.08));
  }
  .dhk8-tsheet__order-folio { font-family: var(--font-mono, ui-monospace); font-size: var(--pos-text-md, 15px); font-weight: var(--pos-weight-semibold, 600); color: var(--pos-fg, #E8EFE9); }
  .dhk8-tsheet__order-total { font-size: var(--pos-text-md, 15px); font-weight: var(--pos-weight-semibold, 600); color: var(--pos-success, #2EA043); margin-left: auto; }
  .dhk8-tsheet__order-when { font-size: var(--pos-text-xs, 11px); color: var(--pos-fg-tertiary, #7E8C82); width: 100%; }

  .dhk8-tsheet__section-label { font-size: var(--pos-text-xs, 11px); font-weight: var(--pos-weight-semibold, 600); letter-spacing: .06em; text-transform: uppercase; color: var(--pos-fg-tertiary, #7E8C82); }

  .dhk8-tsheet__notifs { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: var(--pos-space-2, 8px); }
  .dhk8-tsheet__notif {
    /* NintaiGO: sin border-left decorativo. El warning se comunica via
       background-tint + border-color full, no con barrita lateral. */
    display: flex; align-items: flex-start; gap: var(--pos-space-2, 8px);
    padding: var(--pos-space-2, 8px) var(--pos-space-3, 12px);
    background: var(--pos-warning-bg, rgba(224,162,60,.14));
    border: 1px solid var(--pos-warning, #E0A23C);
  }
  .dhk8-tsheet__notif-icon { font-size: 18px; line-height: 1.2; }
  .dhk8-tsheet__notif-body { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 2px; }
  .dhk8-tsheet__notif-topic { font-size: var(--pos-text-sm, 13px); font-weight: var(--pos-weight-semibold, 600); color: var(--pos-fg, #E8EFE9); }
  .dhk8-tsheet__notif-note { font-size: var(--pos-text-xs, 12px); color: var(--pos-fg-secondary, #B6BFB8); font-style: italic; }
  .dhk8-tsheet__notif-when { font-size: 11px; color: var(--pos-fg-tertiary, #7E8C82); }
  .dhk8-tsheet__notif-btn {
    align-self: center; flex-shrink: 0;
    background: transparent; color: var(--pos-warning, #E0A23C);
    border: 1px solid color-mix(in oklch, var(--pos-warning, #E0A23C) 55%, transparent);
    padding: 5px 9px; font: inherit; font-size: 12px; cursor: pointer;
    display: inline-flex; align-items: center; gap: 4px; white-space: nowrap;
  }
  .dhk8-tsheet__notif-btn:disabled { opacity: .45; cursor: not-allowed; }

  .dhk8-tsheet__statuses { display: grid; grid-template-columns: repeat(2, 1fr); gap: var(--pos-space-2, 8px); }
  .dhk8-tsheet__status-btn {
    display: inline-flex; align-items: center; justify-content: center; gap: var(--pos-space-2, 8px);
    min-height: var(--pos-touch-target, 44px); cursor: pointer; font: inherit;
    font-size: var(--pos-text-sm, 13px); font-weight: var(--pos-weight-medium, 500);
    background: var(--pos-surface, #161C18); color: var(--pos-fg, #E8EFE9);
    border: 1px solid var(--pos-separator-strong, rgba(255,255,255,.14));
  }
  .dhk8-tsheet__status-btn:active { background: var(--pos-elevated, #1E2520); }
  /* DOT — única excepción autorizada al rule de no-radius. */
  .dhk8-tsheet__status-dot { width: 9px; height: 9px; border-radius: var(--radius-dot); flex: 0 0 auto; background: var(--pos-fg-tertiary, #7E8C82); }
  .dhk8-tsheet__status-btn--available .dhk8-tsheet__status-dot { background: var(--pos-success, #2EA043); }
  .dhk8-tsheet__status-btn--occupied .dhk8-tsheet__status-dot { background: var(--pos-danger, #E5484D); }
  .dhk8-tsheet__status-btn--reserved .dhk8-tsheet__status-dot { background: var(--pos-warning, #E0A23C); }
  .dhk8-tsheet__status-btn--cleaning .dhk8-tsheet__status-dot { background: var(--pos-info, #4493F8); }
  .dhk8-tsheet__status-btn--unavailable .dhk8-tsheet__status-dot { background: var(--pos-fg-tertiary, #7E8C82); }

  .dhk8-tsheet__edit { display: grid; grid-template-columns: repeat(2, 1fr); gap: var(--pos-space-3, 12px); }
  .dhk8-tsheet__field { display: flex; flex-direction: column; gap: var(--pos-space-1, 4px); font-size: var(--pos-text-xs, 11px); color: var(--pos-fg-tertiary, #7E8C82); }
  .dhk8-tsheet__field input, .dhk8-tsheet__field select {
    min-height: var(--pos-touch-target, 44px); font: inherit; font-size: var(--pos-text-md, 15px);
    background: var(--pos-surface, #161C18); color: var(--pos-fg, #E8EFE9);
    border: 1px solid var(--pos-separator-strong, rgba(255,255,255,.14));
    padding: 0 var(--pos-space-3, 12px);
  }
  /* Numeric stepper inside TableSheet edit fields. */
  .dhk8-tsheet__num { display: flex; gap: 6px; align-items: stretch; }
  .dhk8-tsheet__num input { flex: 1; min-width: 0; text-align: center; -moz-appearance: textfield; }
  .dhk8-tsheet__num input::-webkit-outer-spin-button,
  .dhk8-tsheet__num input::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
  .dhk8-tsheet__num-btn {
    min-width: var(--pos-touch-target, 44px); min-height: var(--pos-touch-target, 44px); cursor: pointer; font: inherit;
    font-size: var(--pos-text-lg, 17px); font-weight: var(--pos-weight-semibold, 600);
    background: var(--pos-surface, #161C18); color: var(--pos-fg, #E8EFE9);
    border: 1px solid var(--pos-separator-strong, rgba(255,255,255,.14));
  }
  .dhk8-tsheet__num-btn:hover:not(:disabled) { color: var(--pos-tint-on, #48C76C); border-color: var(--pos-tint, #1F8B3A); }
  .dhk8-tsheet__num-btn:disabled { opacity: .4; cursor: default; }
  .dhk8-tsheet__danger, .dhk8-tsheet__primary {
    min-height: var(--pos-touch-target, 44px); cursor: pointer; font: inherit;
    font-size: var(--pos-text-md, 15px); font-weight: var(--pos-weight-semibold, 600);
    border: 1px solid transparent;
  }
  .dhk8-tsheet__danger { background: transparent; color: var(--pos-danger, #E5484D); border-color: var(--pos-danger, #E5484D); }
  .dhk8-tsheet__actions { display: flex; flex-direction: column; gap: var(--pos-space-2, 8px); }
  .dhk8-tsheet__primary { background: var(--pos-tint, #1F8B3A); color: #fff; }
  .dhk8-tsheet__primary:active { filter: brightness(.92); }

  /* Lane #165 — bloque guest (joinCode compartible). */
  .dhk8-tsheet__guest {
    display: flex; flex-direction: column; gap: var(--pos-space-2, 8px);
    padding: var(--pos-space-3, 12px);
    background: var(--pos-surface, #161C18);
    border: 1px solid var(--pos-separator-strong, rgba(255,255,255,.14));
    border-radius: var(--pos-radius-md, 8px);
  }
  .dhk8-tsheet__guest-head { display: flex; justify-content: space-between; align-items: baseline; }
  .dhk8-tsheet__guest-covers {
    display: inline-flex; align-items: center; gap: 4px;
    font-family: var(--font-mono, ui-monospace); font-size: var(--pos-text-xs, 11px);
    color: var(--pos-fg-tertiary, #7E8C82);
  }
  .dhk8-tsheet__guest-code-row { display: flex; gap: var(--pos-space-2, 8px); align-items: stretch; }
  .dhk8-tsheet__guest-code {
    flex: 1; min-height: 56px;
    display: inline-flex; align-items: center; gap: var(--pos-space-2, 8px);
    padding: 0 var(--pos-space-3, 12px);
    background: var(--pos-tint-soft, rgba(31,139,58,.10));
    border: 2px solid var(--pos-tint, #1F8B3A);
    color: var(--pos-tint-on, #48C76C);
    font: inherit; cursor: pointer; position: relative;
    border-radius: var(--pos-radius-md, 8px);
    transition: filter 120ms ease;
  }
  .dhk8-tsheet__guest-code:hover { filter: brightness(1.1); }
  .dhk8-tsheet__guest-code:active { transform: scale(.98); }
  .dhk8-tsheet__guest-code-val {
    font-family: var(--font-mono, ui-monospace);
    font-size: var(--pos-text-2xl, 26px);
    font-weight: var(--pos-weight-bold, 700);
    letter-spacing: 0.12em;
    flex: 1;
  }
  .dhk8-tsheet__guest-copied {
    position: absolute; top: -10px; right: 8px;
    padding: 2px 8px; font-size: 10px;
    background: var(--pos-tint, #1F8B3A); color: #fff;
    border-radius: var(--pos-radius-full, 999px);
  }
  .dhk8-tsheet__guest-share {
    min-height: 56px; padding: 0 var(--pos-space-3, 12px);
    display: inline-flex; align-items: center; gap: 6px;
    background: var(--pos-surface, #161C18);
    color: var(--pos-fg-secondary, #B6BFB8);
    border: 1px solid var(--pos-separator-strong, rgba(255,255,255,.14));
    font: inherit; font-size: var(--pos-text-sm, 13px);
    cursor: pointer;
    border-radius: var(--pos-radius-md, 8px);
  }
  .dhk8-tsheet__guest-share:hover { color: var(--pos-fg, #E8EFE9); border-color: var(--pos-fg-tertiary, #7E8C82); }
  .dhk8-tsheet__guest-hint { margin: 0; font-size: var(--pos-text-xs, 11px); color: var(--pos-fg-tertiary, #7E8C82); }

  /* Lane #165 — banner de mesa bloqueada (status no editable). */
  .dhk8-tsheet__lock-banner {
    display: flex; align-items: flex-start; gap: var(--pos-space-2, 8px);
    padding: var(--pos-space-3, 12px);
    background: var(--pos-warning-bg, rgba(224,162,60,.10));
    border: 1px dashed var(--pos-warning, #E0A23C);
    color: var(--pos-warning, #E0A23C);
    font-size: var(--pos-text-xs, 12px); line-height: 1.4;
    border-radius: var(--pos-radius-md, 8px);
  }
  .dhk8-tsheet__lock-banner strong { color: var(--pos-fg, #E8EFE9); font-weight: var(--pos-weight-semibold, 600); }
  /* Lane #172 — botón escape hatch para forzar liberar la mesa (manager-gated). */
  .dhk8-tsheet__force-clear {
    min-height: var(--pos-touch-target, 44px);
    margin-top: var(--pos-space-2, 8px);
    padding: 0 var(--pos-space-4, 16px);
    background: transparent;
    border: 1px solid var(--pos-danger, #E5484D);
    color: var(--pos-danger, #E5484D);
    border-radius: var(--pos-radius-md, 8px);
    font: inherit; font-size: var(--pos-text-sm, 13px);
    font-weight: var(--pos-weight-medium, 500); cursor: pointer;
    transition: background var(--pos-duration-fast, 120ms) ease;
  }
  .dhk8-tsheet__force-clear:hover {
    background: color-mix(in oklch, var(--pos-danger, #E5484D) 12%, transparent);
  }

  /* Lane #165 — botones de acción en el modal (Abrir comanda / Mover). */
  .dhk8-tsheet__action {
    min-height: var(--pos-touch-target, 48px); padding: 0 var(--pos-space-4, 16px);
    display: inline-flex; align-items: center; justify-content: center; gap: 6px;
    background: var(--pos-surface, #161C18);
    color: var(--pos-fg, #E8EFE9);
    border: 1px solid var(--pos-separator-strong, rgba(255,255,255,.14));
    font: inherit; font-size: var(--pos-text-md, 15px); font-weight: var(--pos-weight-semibold, 600);
    cursor: pointer;
    border-radius: var(--pos-radius-md, 8px);
  }
  .dhk8-tsheet__action:hover { background: var(--pos-elevated, #1E2520); }
  .dhk8-tsheet__action--primary { background: var(--pos-tint, #1F8B3A); border-color: var(--pos-tint, #1F8B3A); color: #fff; }
  .dhk8-tsheet__action--primary:hover { filter: brightness(1.1); background: var(--pos-tint, #1F8B3A); }
`
